import React from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import PropTypes from 'prop-types';
import Util from '../../../../../forecast-app/shared/util/util';
import TooltipContainer from '../../../../../forecast-app/shared/components/tooltips/tooltip_container';
import InformationIcon from '../../../../../images/information-icon';
import ForecastTooltip from '../../../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import {ForecastTooltipFormulaRenderer} from '../../../../ForecastTooltipFormulaRenderer';
import {offsetTooltip} from '../BudgetTooltips';

export const ProjectFinancials = injectIntl(props => {
	const {intl, project, currencySymbol, tooltip, isFixedPrice, isFixedPriceV2, billableExpensesTotal, invoiceAmountTotal, invoicePaid, isUsingLocks, lockedWorkTotal, lockedExpenseTotal, lockedOffsetTotal, valueBasedProgress, unInvoicedTotal} =
		props;
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();
	const {formatMessage} = intl;
	const {costActualPerDay, remainingCost, priceActualPerDay, reportedBillablePrice} = project;

	const actualRevenue = priceActualPerDay.reduce((total, acc) => total + acc.value, 0);
	const actualCost = costActualPerDay.reduce((total, acc) => total + acc.value, 0);

	const fixedPriceRevenue = project.budget;
	const fixedPriceCost = actualCost + remainingCost;
	const fixedPriceProfit = fixedPriceRevenue - fixedPriceCost;
	const fixedPriceSpent = actualRevenue;
	const fixedPriceRemaining = fixedPriceRevenue - fixedPriceSpent;

	const budgetVsTotalRateValue = project.financialNumbers?.budgetVsTotalRateValue;
	const totalRateValue = project.financialNumbers?.totalRateValue;
	const fixedPriceRevenueFormatted = Util.getFormattedNumberWithCurrency(currencySymbol, fixedPriceRevenue, intl);
	const totalRateValueFormatted = Util.getFormattedNumberWithCurrency(currencySymbol, totalRateValue, intl);
	const budgetVsTotalRateValueTooltip =
		budgetVsTotalRateValue > 0 ? (
			<FormattedMessage
				id="project_budget.budgetVsTotalValue.budgetHigherDescription"
				values={{
					highlight: chunk => <span style={{fontWeight: 'bold', color: '#00ac51'}}>{chunk}</span>,
				}}
			/>
		) : (
			<FormattedMessage
				id="project_budget.budgetVsTotalValue.budgetLowerDescription"
				values={{
					highlight: chunk => <span style={{fontWeight: 'bold', color: '#fd3027'}}>{chunk}</span>,
				}}
			/>
		);

	const budgetVsTotalRateValueRow = budgetVsTotalRateValue && isFixedPriceV2 && (
		<div className="billability-item">
			<ForecastTooltip
				maxWidth="400px"
				content={
					<ForecastTooltipFormulaRenderer
						items={[
							{
								title: formatMessage({id: 'project_budget.budgetVsTotalValue.longTitle'}),
							},
							{
								description: budgetVsTotalRateValueTooltip,
							},
							{
								details: [formatMessage({id: 'common.fixed_price'}), '=', fixedPriceRevenueFormatted],
							},
							{
								details: [formatMessage({id: 'project_budget.budgetVsTotalValue.totalValue'}), '=', totalRateValueFormatted],
							},
						]}
						translatedMessage={true}
					/>
				}
			>
				<div className="name">{formatMessage({id: 'project_budget.budgetVsTotalValue.shortTitle'})}</div>
				<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, budgetVsTotalRateValue, intl)}</div>
			</ForecastTooltip>
		</div>
	);

	return (
		<div className="graph-side-container" data-userpilot="budget-overview">
			<div className="project-billability">
				<div className="section-title">
					{!isFixedPriceV2 ? (
						<>
							{formatMessage({id: isFixedPrice ? 'common.fixed_price' : 'project_budget.actual_billability'})}
							{isFixedPrice ? (
								<TooltipContainer triangleLocation={'right'} infoText={tooltip} tooltipDuration={5 * 60 * 1000} trackingOptions={{helpIcon: 'Fixed price', placement: 'top'}} translatedMessage={true}>
									<InformationIcon />
								</TooltipContainer>
							) : null}
						</>
					) : null}
				</div>
				{isFixedPrice && (!isFixedPriceV2 || !isUsingLocks) ? (
					<div className="billability-list fixed-price">
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.revenue'})}</div>
							<div className="total">{fixedPriceRevenueFormatted}</div>
						</div>
						{!hasLimitedFinancialAccess && (
							<div className="billability-item">
								<div className="name">{formatMessage({id: 'common.cost'})}</div>
								<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, fixedPriceCost, intl)}</div>
							</div>
						)}
						{!hasLimitedFinancialAccess && (
							<div className="billability-item">
								<div className="name">{formatMessage({id: 'common.profit'})}</div>
								<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, fixedPriceProfit, intl)}</div>
							</div>
						)}
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.fixed_price_spent'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, fixedPriceSpent, intl)}</div>
						</div>
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.fixed_price_remaining'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, fixedPriceRemaining, intl)}</div>
						</div>
						{budgetVsTotalRateValueRow}
					</div>
				) : isFixedPriceV2 ? (
					<div className="billability-list">
						<div className="billability-item">
							<ForecastTooltip
								maxWidth="400px"
								content={
									<ForecastTooltipFormulaRenderer
										items={[
											{
												title: formatMessage({id: 'common.progress'}),
												details: [formatMessage({id: 'project_budget.done_task_value'}), '/', formatMessage({id: 'project_budget.total_task_value'})],
											},
										]}
										translatedMessage={true}
									/>
								}
							>
								<div className="name">{formatMessage({id: 'common.progress'})}</div>
								<div className="total">{Util.getFormattedPercentage(valueBasedProgress, intl)}</div>
							</ForecastTooltip>
						</div>
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.closed_billable_work'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, lockedWorkTotal, intl)}</div>
						</div>
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.closed_billable_expenses'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, lockedExpenseTotal, intl)}</div>
						</div>
						{+lockedOffsetTotal !== 0 && (
							<div className="billability-item">
								<ForecastTooltip maxWidth="600px" placement={'left'} content={<ForecastTooltipFormulaRenderer items={offsetTooltip(formatMessage)} translatedMessage={true} />}>
									<div className="name">{formatMessage({id: 'project_budget.total_offset'})}</div>
									<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, lockedOffsetTotal, intl)}</div>
								</ForecastTooltip>
							</div>
						)}
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'common.invoiced'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceAmountTotal, intl)}</div>
						</div>
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.invoice_paid'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, invoicePaid, intl)}</div>
						</div>
						{budgetVsTotalRateValueRow}
					</div>
				) : (
					<div className="billability-list">
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.billable_hours'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, reportedBillablePrice, intl)}</div>
						</div>
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'expenses.billable_expenses'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, billableExpensesTotal, intl)}</div>
						</div>

						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.invoiceable_total'})}</div>
							<div className="total">
								{Util.getFormattedNumberWithCurrency(
									currencySymbol,
									priceActualPerDay.reduce((total, acc) => total + acc.value, 0),
									intl
								)}
							</div>
						</div>

						<div className="billability-item">
							<div className="name">{formatMessage({id: 'common.invoiced'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceAmountTotal, intl)}</div>
						</div>
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.uninvoiced'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, unInvoicedTotal, intl)}</div>
						</div>
						<div className="billability-item">
							<div className="name">{formatMessage({id: 'project_budget.invoice_paid'})}</div>
							<div className="total">{Util.getFormattedNumberWithCurrency(currencySymbol, invoicePaid, intl)}</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
});

ProjectFinancials.props = {
	project: PropTypes.object.isRequired,
	currencySymbol: PropTypes.string.isRequired,
	tooltip: PropTypes.array.isRequired,
};
